import { Button, Card, Empty, List, Popconfirm, Space, Tag, theme, Tooltip, Typography } from 'antd';
import { FC, ReactElement } from 'react';
import { ExclamationCircle, LockFill, PlusLg, Trash } from 'react-bootstrap-icons';

import { DomainPunycode, RegluingIcon } from '@/common/components';
import { DomainRegluing, DomainRegluingsCreateForm, DomainVisitors } from '@/modules/Domains';

interface Props {
  domainId: string;
  siteId: string;
  regluings: DomainRegluing[];
  isRegluingsLoading: boolean;
  isRegluingFormVisible: boolean;
  isButtonsDisabled: boolean;
  handleToggleRegluingFormVisibility: () => void;
  handleDeleteRegluing: (regluingId: string) => () => void;
}

export const DomainRegluingsView: FC<Props> = ({
  domainId,
  siteId,
  regluings,
  isRegluingsLoading,
  isRegluingFormVisible,
  isButtonsDisabled,
  handleToggleRegluingFormVisibility,
  handleDeleteRegluing
}) => {
  const { token } = theme.useToken();

  return (
    <Card
      size="small"
      title="Переклейка"
      extra={
        !isRegluingFormVisible && (
          <Button
            type="link"
            disabled={ isButtonsDisabled }
            icon={ <PlusLg size={ 18 }/> }
            onClick={ handleToggleRegluingFormVisibility }
          />
        )
      }
    >
      { !isRegluingFormVisible && (
        <>
          { !regluings.length && (
            <Empty
              description={ <Typography.Text type="secondary">Переклейки відсутні</Typography.Text> }
              image={ <RegluingIcon size={ 40 }/> }
            />
          ) }

          { !!regluings.length && (
            <List
              dataSource={ regluings }
              loading={ isRegluingsLoading }
              size="small"
              renderItem={ (item): ReactElement => (
                <List.Item>
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space direction="vertical">
                      { item.forVisitor === DomainVisitors.GoogleBot && <Tag color="geekblue">Google Bot</Tag> }
                      { (item.forVisitor === DomainVisitors.Any || item.forVisitor === DomainVisitors.Person) && <Tag color="geekblue">Будь-хто</Tag> }
                      <Typography.Title
                        level={ 5 }
                        style={{
                          marginBottom: 0,
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          maxWidth: 285,
                          textOverflow: 'ellipsis',
                          overflow: 'hidden'
                        }}
                      >
                        <DomainPunycode domainName={ item.targetDomain.name }/>
                      </Typography.Title>
                    </Space>

                    { item.isReservedByAutoRegluing && (
                      <Tooltip title="Зарезервовано автоматичною переклейкою" placement="left">
                        <Button type="text" danger icon={ <LockFill/> }/>
                      </Tooltip>
                    ) }

                    { !item.isReservedByAutoRegluing && (
                      <Popconfirm
                        disabled={ item.isReservedByAutoRegluing || isButtonsDisabled }
                        title="Видалити цю переклейку?"
                        onConfirm={ handleDeleteRegluing(item.id) }
                        okType="danger"
                        okText="Так"
                        icon={ <ExclamationCircle color={ token.colorError }/> }
                        placement="right"
                      >
                        <Button
                          type="text"
                          danger
                          icon={ <Trash/> }
                          disabled={ item.isReservedByAutoRegluing || isButtonsDisabled }
                        />
                      </Popconfirm>
                    ) }
                  </Space>
                </List.Item>
              ) }
            />
          ) }
        </>
      ) }

      {
        isRegluingFormVisible && (
          <DomainRegluingsCreateForm
            domainId={ domainId }
            siteId={ siteId }
            handleToggleRegluingFormVisibility={ handleToggleRegluingFormVisibility }
          />
        )
      }
    </Card>
  );
};
